import { useMutation, useQuery, NetworkStatus } from '@apollo/client'
import { isEmpty } from 'lodash'
import { ReactElement, useEffect } from 'react'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { useParams, useNavigate } from 'react-router-dom'

import Card from '@10xLabs/react-web/components/Card'
import { Loader } from '@10xLabs/react-web/components/Loader'
import { Button } from 'components/v2/Button'
import EmptyState from 'components/v2/EmptyState'
import FormLayoutV2 from 'components/v2/FormLayout'
import { ForbiddenView } from 'features/App/components/ForbiddenView'
import { withLastUrl } from 'features/App/components/WithLastUrl'
import { NewBusinessSite } from 'features/BusinessSites/components/NewBusinessSite'
import { INewBusinessSiteValues } from 'features/BusinessSites/types'
import { IBusinessSiteContact } from 'features/BusinessSites/types'
import {
  mapBusinessSiteToForm,
  mapContactsToMutation,
  mapFormValuesToKreditSettingsMutation
} from 'features/BusinessSites/utils'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { GET_BUSINESS_SITE } from 'features/BusinessSites/queries-legacy'
import {
  CHANGE_BUSINESS_SITE,
  CHANGE_BUSINESS_SITE_CONTACTS,
  CREATE_BUSINESS_SITE_CONTACTS,
  DELETE_BUSINESS_SITE_CONTACTS
} from 'features/BusinessSites/mutations-legacy'
import { GTBusinessSite } from 'gql/types.generated'
import { useLogin } from 'features/Login/hooks'
import { getDifference, getEntitiesDifference } from 'utils/object'
import { canWrite } from 'features/Users/utils'
import { CHANGE_BUSINESS_SITE_KREDIT_SETTINGS } from 'features/Sites/mutations-legacy'

const messages = defineMessages({
  helmet: {
    id: 'businessSites.edit.helmet',
    defaultMessage: 'Edit Business Site | Nexbus Admin'
  }
})

const EditBusinessSiteView = (): ReactElement => {
  const navigate = useNavigate()
  const { siteId } = useParams()
  const intl = useIntl()
  const login = useLogin()
  const canEditBusinessSite = canWrite(FEATURES.BUSINESS_SITE, login.accessToken.policies)

  useEffect(() => {
    document.title = intl.formatMessage(messages.helmet)
  }, [])

  const {
    loading: queryLoading,
    error: queryError,
    data: queryData,
    refetch,
    networkStatus
  } = useQuery<{ businessSite: GTBusinessSite }>(GET_BUSINESS_SITE, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    skip: !canEditBusinessSite,
    variables: {
      id: siteId
    }
  })
  const [changeBusinessSite, { loading: loadingChangeBusinessSite, error: errorChangeBusinessSite }] =
    useMutation(CHANGE_BUSINESS_SITE)
  const [createContacts, { loading: loadingCreateContacts, error: errorCreateContacts }] =
    useMutation(CREATE_BUSINESS_SITE_CONTACTS)
  const [deleteContacts, { loading: loadingDeleteContacts, error: errorDeleteContacts }] =
    useMutation(DELETE_BUSINESS_SITE_CONTACTS)
  const [changeContacts, { loading: loadingChangeContacts, error: errorChangeContacts }] =
    useMutation(CHANGE_BUSINESS_SITE_CONTACTS)
  const [changeKreditSettings, { loading: loadingChangeKreditSettings, error: errorChangeKreditSettings }] =
    useMutation(CHANGE_BUSINESS_SITE_KREDIT_SETTINGS)

  const loading =
    loadingChangeBusinessSite ||
    loadingCreateContacts ||
    loadingDeleteContacts ||
    loadingChangeContacts ||
    loadingChangeKreditSettings
  const error =
    errorChangeBusinessSite ||
    errorCreateContacts ||
    errorDeleteContacts ||
    errorChangeContacts ||
    errorChangeKreditSettings

  if (!canEditBusinessSite) {
    return <ForbiddenView feature={FEATURES.BUSINESS_SITE} />
  }

  let content: ReactElement
  if (networkStatus === NetworkStatus.refetch || queryLoading) {
    content = (
      <Card.Content noHeader noFooter>
        <EmptyState.Container>
          <Loader />
        </EmptyState.Container>
      </Card.Content>
    )
  } else if (queryError) {
    content = (
      <Card.Content noHeader noFooter>
        <EmptyState.Container>
          <EmptyState.Description>
            <FormattedMessage
              id="businessSites.edit.error.description"
              defaultMessage="There was an error while loading your site"
            />
          </EmptyState.Description>
          <EmptyState.Action>
            <Button
              onClick={(): void => {
                refetch()
              }}
              kind="primary"
            >
              <FormattedMessage id="businessSites.edit.error.action" defaultMessage="Reload your site" />
            </Button>
          </EmptyState.Action>
        </EmptyState.Container>
      </Card.Content>
    )
  } else {
    const handleSubmit = async (newValues: INewBusinessSiteValues): Promise<void> => {
      try {
        const newContacts = mapContactsToMutation(newValues.contacts)
        const oldContacts: IBusinessSiteContact[] = mapContactsToMutation(
          (queryData?.businessSite.contacts as IBusinessSiteContact[]) ?? []
        )
        const {
          createdEntities: createdContacts,
          editedEntities: editedContacts,
          deletedEntities: deletedContacts,
          hasDifferences: hasContactsDifferences
        } = getEntitiesDifference<IBusinessSiteContact>(newContacts, oldContacts)

        const oldBusinessSite = {
          name: queryData?.businessSite.name ?? '',
          cityID: queryData?.businessSite.city?.id || null
        }
        const newBusinessSite = {
          name: newValues.name,
          cityID: newValues.city.id || null
        }
        const businessSitePayload = getDifference(newBusinessSite, oldBusinessSite)

        const kreditSettingsPayload = getDifference(
          mapBusinessSiteToForm(queryData?.businessSite as GTBusinessSite).kreditsSettings,
          newValues.kreditsSettings
        )

        if (hasContactsDifferences) {
          if (!isEmpty(deletedContacts)) {
            await deleteContacts({
              variables: {
                id: siteId,
                input: {
                  contactIDs: deletedContacts.map(({ id }) => id)
                }
              }
            })
          }
          if (!isEmpty(editedContacts)) {
            await changeContacts({
              variables: {
                id: siteId,
                input: {
                  contacts: editedContacts
                }
              }
            })
          }
          if (!isEmpty(createdContacts)) {
            await createContacts({
              variables: {
                id: siteId,
                input: {
                  contacts: createdContacts
                }
              }
            })
          }
        }

        if (!isEmpty(kreditSettingsPayload)) {
          const kreditSettingsInput = mapFormValuesToKreditSettingsMutation(
            newValues,
            siteId as string,
            newValues.client.id
          )
          await changeKreditSettings({
            variables: {
              input: kreditSettingsInput
            }
          })
        }

        if (!isEmpty(businessSitePayload)) {
          await changeBusinessSite({
            variables: {
              id: siteId,
              input: {
                ...businessSitePayload
              }
            }
          })
        }
        navigate('/business-sites')
      } catch (ex) {
        // Do nothing
      }
    }

    content = (
      <NewBusinessSite
        businessClient={queryData?.businessSite.client}
        businessSite={mapBusinessSiteToForm(queryData?.businessSite as GTBusinessSite)}
        onSubmit={handleSubmit}
        loading={loading}
        error={error}
        isEdit
      />
    )
  }

  return <FormLayoutV2.Container>{content}</FormLayoutV2.Container>
}

export default withLastUrl(EditBusinessSiteView)
